import QRCode from "react-qr-code";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";

const PRINT_STYLES = `
  body{font-family:'IBM Plex Sans',sans-serif;text-align:center;padding:40px;margin:0;}
  .tag{font-family:monospace;font-weight:bold;font-size:14px;border:1px solid #18181b;display:inline-block;padding:4px 10px;border-radius:2px;margin-bottom:8px;}
  h2{font-size:22px;margin:8px 0;letter-spacing:-0.01em;}
  .sub{font-size:11px;color:#71717a;letter-spacing:0.18em;text-transform:uppercase;}
  .qr{margin:16px auto;}
  .brand{margin-top:24px;font-size:10px;color:#a1a1aa;letter-spacing:0.2em;text-transform:uppercase;}
`;

const buildPrintDocument = (win, asset, qrSvgElement) => {
  // Safe DOM construction (no document.write, no innerHTML for user data)
  const d = win.document;
  d.title = `${asset.tag} · ${asset.name}`;

  const styleEl = d.createElement("style");
  styleEl.textContent = PRINT_STYLES;
  d.head.appendChild(styleEl);

  const tagEl = d.createElement("div");
  tagEl.className = "tag";
  tagEl.textContent = asset.tag; // textContent → escapes by default
  d.body.appendChild(tagEl);

  const h2 = d.createElement("h2");
  h2.textContent = asset.name;
  d.body.appendChild(h2);

  const sub = d.createElement("div");
  sub.className = "sub";
  sub.textContent = `${asset.category}${asset.subcategory ? " · " + asset.subcategory : ""}`;
  d.body.appendChild(sub);

  const qrWrap = d.createElement("div");
  qrWrap.className = "qr";
  if (qrSvgElement) {
    // cloneNode is safe — it copies the trusted SVG generated by react-qr-code in our own DOM
    qrWrap.appendChild(qrSvgElement.cloneNode(true));
  }
  d.body.appendChild(qrWrap);

  const brand = d.createElement("div");
  brand.className = "brand";
  brand.textContent = "FieldOps · Scan to open";
  d.body.appendChild(brand);
};

export default function AssetQrDialog({ asset, open, onOpenChange }) {
  const value = asset
    ? `${window.location.origin}/assets?focus=${asset.id}`
    : "";

  const print = () => {
    if (!asset) return;
    const w = window.open("", "_blank", "width=420,height=520");
    if (!w) return;
    const svg = document.getElementById(`qr-svg-${asset.id}`);
    buildPrintDocument(w, asset, svg);
    // Defer print so the new window finishes layout
    setTimeout(() => {
      try {
        w.focus();
        w.print();
      } catch (err) {
        console.error("Print failed", err);
      }
    }, 50);
  };

  if (!asset) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-sm" data-testid="asset-qr-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{asset.tag}</DialogTitle>
        </DialogHeader>
        <div className="text-center">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-1">
            {asset.category} · {asset.subcategory}
          </div>
          <h3 className="font-display font-bold text-lg mb-4">{asset.name}</h3>
          <div className="bg-white p-4 border border-zinc-300 inline-block rounded-sm">
            <QRCode
              id={`qr-svg-${asset.id}`}
              value={value}
              size={192}
              level="H"
              bgColor="#FFFFFF"
              fgColor="#09090B"
              data-testid="qr-svg"
            />
          </div>
          <div className="font-mono text-[10px] text-zinc-500 mt-3 break-all px-2">
            {value}
          </div>
          <Button
            data-testid="qr-print-btn"
            onClick={print}
            className="mt-4 w-full bg-zinc-950 hover:bg-zinc-800 rounded-sm font-bold"
          >
            <Printer className="h-4 w-4 mr-1" /> Print Label
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
